import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Field, Text, Textarea, Toggle, StringListEditor } from "@/admin/components/AdminField";
import ImageUpload, { GalleryUpload } from "@/admin/components/ImageUpload";

/** Aceita string enquanto o usuário digita (permitindo "-", "-19", "-19.9", ""), retornando string.
 *  Normaliza vírgula → ponto e limita a caracteres válidos. Persistimos como string editável;
 *  na hora do submit convertemos para número. */
function parseCoord(v) {
  if (v === "" || v === null || v === undefined) return null;
  // Normalize vírgula → ponto, remove caracteres inválidos
  const cleaned = String(v).replace(",", ".").replace(/[^0-9.\-]/g, "");
  return cleaned;
}

function toNumberOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

const EMPTY = {
  slug: "", name: "", state: "", short_description: "", description: "",
  cover_image: "", gallery: [],
  where_to_stay: [], where_to_eat: [], what_to_visit: [], best_roads: [],
  how_to_get: "", points_of_interest: [], related_route_slugs: [],
  video_url: "", google_maps_url: "", latitude: null, longitude: null,
  published: true, seo: { meta_title: "", meta_description: "", og_image: "" },
};

export default function CityForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      adminApi.get(`/admin/cities/${id}`).then((r) => setF({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => toast.error("Falha ao carregar")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const setSeo = (patch) => setF((s) => ({ ...s, seo: { ...s.seo, ...patch } }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, latitude: toNumberOrNull(f.latitude), longitude: toNumberOrNull(f.longitude) };
      if (editing) await adminApi.put(`/admin/cities/${id}`, payload);
      else await adminApi.post(`/admin/cities`, payload);
      toast.success(editing ? "Cidade atualizada" : "Cidade criada");
      navigate("/admin/cidades");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[#B8B8B8]"><Loader2 className="inline animate-spin" size={16} /> Carregando…</div>;

  return (
    <form onSubmit={submit} className="space-y-8 max-w-5xl" data-testid="city-form">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin/cidades" className="text-[10px] tracking-[0.25em] uppercase text-[#B8B8B8] inline-flex items-center gap-2 hover:text-[#C6A762]">
            <ArrowLeft size={12} /> Voltar
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl mt-3">{editing ? "Editar cidade" : "Nova cidade"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Toggle value={f.published} onChange={(v) => set({ published: v })} label="Publicada" />
          <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 ml-auto sm:ml-0">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      </div>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Nome"><Text value={f.name} onChange={(v) => set({ name: v, slug: f.slug || v })} required /></Field>
          <Field label="Slug"><Text value={f.slug} onChange={(v) => set({ slug: v })} /></Field>
          <Field label="Estado"><Text value={f.state} onChange={(v) => set({ state: v })} required /></Field>
        </div>
        <Field label="Descrição curta"><Textarea rows={2} value={f.short_description} onChange={(v) => set({ short_description: v })} /></Field>
        <Field label="Descrição"><Textarea rows={5} value={f.description} onChange={(v) => set({ description: v })} /></Field>
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <ImageUpload label="Foto principal" value={f.cover_image} onChange={(v) => set({ cover_image: v })} folder="rotta/cities" />
        <GalleryUpload label="Galeria" value={f.gallery} onChange={(v) => set({ gallery: v })} folder="rotta/gallery" />
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Onde comer"><StringListEditor value={f.where_to_eat} onChange={(v) => set({ where_to_eat: v })} /></Field>
          <Field label="Onde ficar"><StringListEditor value={f.where_to_stay} onChange={(v) => set({ where_to_stay: v })} /></Field>
          <Field label="O que visitar"><StringListEditor value={f.what_to_visit} onChange={(v) => set({ what_to_visit: v })} /></Field>
          <Field label="Melhores estradas"><StringListEditor value={f.best_roads} onChange={(v) => set({ best_roads: v })} /></Field>
          <Field label="Pontos de interesse"><StringListEditor value={f.points_of_interest} onChange={(v) => set({ points_of_interest: v })} /></Field>
          <Field label="Rotas relacionadas (slugs)"><StringListEditor value={f.related_route_slugs} onChange={(v) => set({ related_route_slugs: v })} /></Field>
        </div>
        <Field label="Como chegar de moto"><Textarea rows={3} value={f.how_to_get} onChange={(v) => set({ how_to_get: v })} /></Field>
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <h2 className="font-display tracking-wider uppercase text-sm text-[#C6A762]">Vídeo & Localização</h2>
        <Field label="Link do vídeo (YouTube)" hint="Deixe vazio para ocultar a seção de vídeo."><Text data-testid="city-video-url" value={f.video_url} onChange={(v) => set({ video_url: v })} placeholder="https://youtube.com/watch?v=…" /></Field>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Link do Google Maps"><Text data-testid="city-google-maps-url" value={f.google_maps_url} onChange={(v) => set({ google_maps_url: v })} placeholder="https://maps.google.com/…" /></Field>
          <Field label="Latitude" hint="Opcional — centraliza o mapa"><Text data-testid="city-latitude" inputMode="decimal" value={f.latitude ?? ""} onChange={(v) => set({ latitude: parseCoord(v) })} placeholder="-19.9245" /></Field>
          <Field label="Longitude" hint="Opcional"><Text data-testid="city-longitude" inputMode="decimal" value={f.longitude ?? ""} onChange={(v) => set({ longitude: parseCoord(v) })} placeholder="-43.9352" /></Field>
        </div>
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <h2 className="font-display tracking-wider uppercase text-sm text-[#C6A762]">SEO</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Meta Title"><Text value={f.seo.meta_title} onChange={(v) => setSeo({ meta_title: v })} /></Field>
          <Field label="OG Image URL"><Text value={f.seo.og_image} onChange={(v) => setSeo({ og_image: v })} /></Field>
          <Field label="Meta Description" cols={2}><Textarea rows={2} value={f.seo.meta_description} onChange={(v) => setSeo({ meta_description: v })} /></Field>
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <Link to="/admin/cidades" className="btn-ghost px-6 py-3 rounded-full text-xs">Cancelar</Link>
        <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
        </button>
      </div>
    </form>
  );
}
